import React, { useState, useRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Input,
  CustomDatePicker,
  SCREENS,
  useMediaQuery,
  useT,
} from '@repo/common';
import PostSection from '../PostSection';
import usePlaceholder from '../usePlaceholder';
import PostRadioGroup from '../PostRadioGroup';
import StartAvailabilityList, {
  START_AVAILABILITY_MANUAL_OPTION,
  START_AVAILABILITY_PLACEHOLDER,
  START_AVAILABILITY_PRESET_OPTIONS,
} from './StartAvailabilityList';
import ResponseSpeedList from './ResponseSpeedList';

import { ReactComponent as InfoIcon } from '@repo/common/assets/icons/16px/icon_16px_info_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';

const ApplicationDeadline = ({ textValues, setTextValues, errors }) => {
  const t = useT();
  const ph = usePlaceholder();
  const isMobile = useMediaQuery(`(max-width: ${SCREENS.tabletH - 0.02}px)`);

  /**
   * 응답 속도 안내 문구.
   * 목록은 Input 전체(메시지 포함) 아래에 뜨므로, 열려 있을 때는 문구를 숨겨
   * 목록이 입력창에 바로 붙게 한다.
   */
  const responseSpeedNotice = (
    <span className='inline-flex items-start gap-0.5 text-grayscale-gray04'>
      <InfoIcon className='mt-[3px] h-4 w-4 shrink-0 text-icon-placeholder' />
      {t(
        'Consistently meeting your response target may unlock the Quick Response badge.',
        '응답 목표를 꾸준히 지키면 빠른 응답 배지를 받을 수 있습니다.',
      )}
    </span>
  );
  const [isStartAvailabilityOpen, setIsStartAvailabilityOpen] = useState(false);
  const [isResponseSpeedOpen, setIsResponseSpeedOpen] = useState(false);
  const [isDeadlineOpen, setIsDeadlineOpen] = useState(false);
  const [isStartAvailabilityManual, setIsStartAvailabilityManual] =
    useState(false);

  const hasCustomStartAvailability =
    isStartAvailabilityManual ||
    (Boolean(textValues.startAvailability) &&
      !START_AVAILABILITY_PRESET_OPTIONS.includes(
        textValues.startAvailability,
      ) &&
      textValues.startAvailability !== START_AVAILABILITY_PLACEHOLDER);

  const refs = useRef({
    deadline: null,
    startAvailability: null,
    responseSpeed: null,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        refs.current.deadline &&
        !refs.current.deadline.contains(event.target)
      ) {
        setIsDeadlineOpen(false);
      }

      if (
        refs.current.startAvailability &&
        !refs.current.startAvailability.contains(event.target)
      ) {
        setIsStartAvailabilityOpen(false);
      }

      if (
        refs.current.responseSpeed &&
        !refs.current.responseSpeed.contains(event.target)
      ) {
        setIsResponseSpeedOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <PostSection title={t('Application deadline', '지원 마감')}>
      <PostRadioGroup
        label={t('Hiring Status', '채용 상태')}
        required
        value={textValues.hiringStatus}
        onChange={(value) =>
          setTextValues((prev) => ({
            ...prev,
            hiringStatus: value,
          }))
        }
        options={[
          { value: 'ongoing', label: t('Ongoing hiring', '상시 채용') },
          { value: 'fixed', label: t('Fixed deadline', '마감일 지정') },
        ]}
      >
        <div
          ref={(el) => (refs.current.deadline = el)}
          className='relative flex items-center'
        >
          <input
            readOnly
            value={
              textValues.deadline
                ? `${t('Apply by', '마감일')} ${new Date(textValues.deadline)
                    .toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })
                    .replace(/\. /g, '.')
                    .replace(/\.$/, '')}`
                : t('Select date', '날짜를 선택하세요')
            }
            onClick={() => setIsDeadlineOpen(true)}
            className={twMerge(
              'px-3 py-1.5 flex items-center justify-center',
              'bg-grayscale-gray00 hover:bg-grayscale-gray01 border border-grayscale-gray02 rounded-md',
              'typo-b13Strong text-grayscale-gray07 cursor-pointer outline-none',
              textValues.hiringStatus !== 'fixed' && 'hidden',
            )}
          />

          {isDeadlineOpen &&
            (() => {
              const picker = (
                <CustomDatePicker
                  selectedDate={textValues.deadline}
                  $isMobile={isMobile}
                  title={t('Set application deadline', '지원 마감일 설정')}
                  onChange={(date) => {
                    setTextValues((prev) => ({
                      ...prev,
                      deadline: date,
                    }));
                    setIsDeadlineOpen(false);
                  }}
                  onClose={() => setIsDeadlineOpen(false)}
                  onClickOutside={() => setIsDeadlineOpen(false)}
                  isVisaExpiryDate
                />
              );

              return isMobile ? (
                picker
              ) : (
                <div className='absolute -top-[50px] z-30'>{picker}</div>
              );
            })()}
        </div>
      </PostRadioGroup>

      <div
        ref={(el) => (refs.current.startAvailability = el)}
        className='relative'
      >
        <Input
          data-validation-field='startAvailability'
          label={t('Start Availability (optional)', '입사 가능 시기 (선택)')}
          value={
            textValues.startAvailability === START_AVAILABILITY_PLACEHOLDER
              ? ''
              : textValues.startAvailability
          }
          state={errors.startAvailability ? 'failed' : 'default'}
          showStatusIcon={false}
          message={errors.startAvailability}
          placeholder={ph(
            hasCustomStartAvailability
              ? t('Enter start availability', '입사 가능 시기를 입력하세요')
              : t(
                  'Select preferred start availability',
                  '희망 입사 시기를 선택하세요',
                ),
          )}
          rightIcon={
            isStartAvailabilityOpen ? (
              <ArrowTopIcon className='text-icon-placeholder' />
            ) : (
              <ArrowBottomIcon className='text-icon-placeholder' />
            )
          }
          readOnly={!hasCustomStartAvailability}
          onChange={(event) => {
            if (!hasCustomStartAvailability) return;
            setTextValues((prev) => ({
              ...prev,
              startAvailability: event.target.value,
            }));
          }}
          onClick={() => {
            if (!hasCustomStartAvailability) {
              setIsStartAvailabilityOpen((prev) => !prev);
            }
          }}
          onRightIconClick={() => setIsStartAvailabilityOpen((prev) => !prev)}
        />
        {isStartAvailabilityOpen && (
          <div className='absolute top-full mt-1 left-0 z-20 w-full'>
            <StartAvailabilityList
              selectedOption={
                hasCustomStartAvailability
                  ? START_AVAILABILITY_MANUAL_OPTION
                  : textValues.startAvailability
              }
              onSelect={(option) => {
                const isManual = option === START_AVAILABILITY_MANUAL_OPTION;
                setIsStartAvailabilityManual(isManual);
                setTextValues((prev) => ({
                  ...prev,
                  startAvailability: isManual ? '' : option,
                }));
                setIsStartAvailabilityOpen(false);
              }}
            />
          </div>
        )}
      </div>

      <div
        ref={(el) => (refs.current.responseSpeed = el)}
        className='flex flex-col gap-2'
      >
        <div className='relative'>
          <Input
            label={t('Response Speed (optional)', '응답 속도 (선택)')}
            value={
              textValues.responseSpeed === 'Select expected response time'
                ? ''
                : textValues.responseSpeed
            }
            placeholder={t(
              'Select expected response time',
              '예상 응답 시간을 선택하세요',
            )}
            rightIcon={
              isResponseSpeedOpen ? (
                <ArrowTopIcon className='text-icon-placeholder' />
              ) : (
                <ArrowBottomIcon className='text-icon-placeholder' />
              )
            }
            message={isResponseSpeedOpen ? undefined : responseSpeedNotice}
            readOnly
            onClick={() => setIsResponseSpeedOpen((prev) => !prev)}
            onRightIconClick={() => setIsResponseSpeedOpen((prev) => !prev)}
          />
          {isResponseSpeedOpen && (
            <div className='absolute top-full mt-1 left-0 z-20 w-full'>
              <ResponseSpeedList
                selectedOption={textValues.responseSpeed}
                onSelect={(option) => {
                  setTextValues((prev) => ({
                    ...prev,
                    responseSpeed: option,
                  }));
                  setIsResponseSpeedOpen(false);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </PostSection>
  );
};

export default ApplicationDeadline;
